import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Stored in API `meal` field as comma-separated values (stable order). */
export const MEAL_TYPE_OPTIONS = ['Breakfast', 'Lunch', 'Snacks', 'Dinner'];

export function parseMealsCsv(csv) {
    if (csv == null || String(csv).trim() === '') return [];
    return String(csv)
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

export function canonicalMealsCsv(csv) {
    const selected = new Set();
    for (const t of parseMealsCsv(csv)) {
        const c = MEAL_TYPE_OPTIONS.find((m) => m.toLowerCase() === t.toLowerCase());
        if (c) selected.add(c);
    }
    return MEAL_TYPE_OPTIONS.filter((m) => selected.has(m)).join(', ');
}

export function allMealsCsv() {
    return MEAL_TYPE_OPTIONS.join(', ');
}

export function isAllMealsSelected(csv) {
    const selected = new Set(parseMealsCsv(canonicalMealsCsv(csv)));
    return MEAL_TYPE_OPTIONS.length > 0 && MEAL_TYPE_OPTIONS.every((m) => selected.has(m));
}

export function toggleAllMeals(csv) {
    return isAllMealsSelected(csv) ? '' : allMealsCsv();
}

export function toggleMealCanonical(csv, meal) {
    const selected = new Set(parseMealsCsv(canonicalMealsCsv(csv)));
    if (selected.has(meal)) selected.delete(meal);
    else selected.add(meal);
    return MEAL_TYPE_OPTIONS.filter((m) => selected.has(m)).join(', ');
}

/** Map a meal token from API to one of MEAL_TYPE_OPTIONS (case-insensitive). */
export function canonicalMealToken(token) {
    const t = String(token ?? '').trim();
    if (!t) return null;
    return MEAL_TYPE_OPTIONS.find((opt) => opt.toLowerCase() === t.toLowerCase()) ?? null;
}

/**
 * Multi-select meal types: button trigger + portaled checklist panel.
 */
export default function MealTypesDropdown({ value, onCsvChange, id: instanceId }) {
    const dropdownId =
        instanceId != null && String(instanceId).trim() !== ''
            ? `meal-dd-${String(instanceId).replace(/[^a-zA-Z0-9_-]/g, '-')}`
            : `meal-dd-${Math.random().toString(36).slice(2, 9)}`;

    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyle, setPanelStyle] = useState({
        position: 'fixed',
        top: 0,
        left: 0,
        width: 240,
        zIndex: 10050,
        visibility: 'hidden',
    });

    const canonicalFromProp = canonicalMealsCsv(value ?? '');
    const [displayCsv, setDisplayCsv] = useState(canonicalFromProp);
    useEffect(() => {
        setDisplayCsv(canonicalFromProp);
    }, [canonicalFromProp]);

    const csv = displayCsv;
    const picked = parseMealsCsv(csv);
    const allSelected = isAllMealsSelected(csv);
    const noSelection = picked.length === 0;
    const labelText = noSelection
        ? 'Select meal'
        : allSelected
          ? 'All'
          : picked.join(', ');

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pad = 8;
        const minW = Math.max(Math.round(r.width), 220);
        let left = r.left;
        if (left + minW > window.innerWidth - pad) {
            left = Math.max(pad, window.innerWidth - minW - pad);
        }
        const maxPanelH = 240;
        let top = r.bottom + 4;
        if (top + maxPanelH > window.innerHeight - pad) {
            top = Math.max(pad, r.top - maxPanelH - 4);
        }
        setPanelStyle({
            position: 'fixed',
            left,
            top,
            width: minW,
            zIndex: 10050,
            visibility: 'visible',
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const onResizeOrScroll = () => updatePosition();
        window.addEventListener('resize', onResizeOrScroll);
        window.addEventListener('scroll', onResizeOrScroll, true);
        return () => {
            window.removeEventListener('resize', onResizeOrScroll);
            window.removeEventListener('scroll', onResizeOrScroll, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const t = e.target;
            if (!(t instanceof Node)) return;
            if (triggerRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const panel =
        open &&
        createPortal(
            <div
                ref={panelRef}
                id={`${dropdownId}-panel`}
                className="daily-menu-meal-floating-panel"
                style={panelStyle}
                role="listbox"
                aria-label="Meal types"
                aria-multiselectable="true"
            >
                <label
                    className="daily-menu-meal-checkbox-row daily-menu-meal-checkbox-row--all"
                    role="option"
                    aria-selected={allSelected}
                >
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                            const next = toggleAllMeals(csv);
                            setDisplayCsv(next);
                            onCsvChange(next);
                        }}
                    />
                    <span>All</span>
                </label>
                {MEAL_TYPE_OPTIONS.map((meal) => (
                        <label
                            key={meal}
                            className="daily-menu-meal-checkbox-row"
                            role="option"
                            aria-selected={picked.includes(meal)}
                        >
                            <input
                                type="checkbox"
                                checked={picked.includes(meal)}
                                onChange={() => {
                                    const next = toggleMealCanonical(csv, meal);
                                    setDisplayCsv(next);
                                    onCsvChange(next);
                                }}
                            />
                            <span>{meal}</span>
                        </label>
                ))}
            </div>,
            document.body
        );

    return (
        <div className="daily-menu-meal-root">
            <button
                type="button"
                id={dropdownId}
                ref={triggerRef}
                className={`daily-menu-meal-trigger ${open ? 'is-open' : ''}`}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={`${dropdownId}-panel`}
                onClick={() => setOpen((o) => !o)}
            >
                <span
                    className={`daily-menu-meal-trigger-label${
                        noSelection ? ' daily-menu-meal-trigger-placeholder' : ''
                    }`}
                    title={noSelection ? '' : labelText}
                >
                    {labelText}
                </span>
            </button>
            {panel}
        </div>
    );
}
